import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  MessageSquare,
  Calendar,
  UserPlus,
  Heart,
  ArrowLeft,
  Loader2,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import apiClient from "../../api/apiClient";
import "./Notifications.css";
import { useNotifications } from "../../context/NotificationContext";
import TopNavBar from "../../components/TopNavBar";
import appIcon from "../../assets/appIcon.svg";
import MobileTopBar from "../../components/MobileTopBar";

const Notifications = () => {
  const navigate = useNavigate();
  // Extract newNotification from context
  const { setUnreadCount, newNotification } = useNotifications();

  const { profile } = useSelector((state) => state.user);
  const [notifications, setNotifications] = useState([]);
  const [activeTab, setActiveTab] = useState("All");
  const [loading, setLoading] = useState(true);

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await apiClient.get(`/notifications/${profile?.id}`);
      if (response.data.status) {
        const data = response.data.data;
        setNotifications(data);
        const count = data.filter((n) => !n.is_read).length;
        setUnreadCount(count);
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (profile?.id) {
      fetchNotifications();
    }
  }, [profile?.id]);

  // Listen for incoming context updates
  useEffect(() => {
    if (newNotification) {
      setNotifications((prev) => {
        // Prevent duplicates in case API fetch and push overlap
        if (prev.some((n) => n.id === newNotification.id)) return prev;
        return [newNotification, ...prev];
      });
    }
  }, [newNotification]);

  const markAllAsRead = async () => {
    try {
      const response = await apiClient.put(
        `/notifications/markAll-notification-asRead/${profile?.id}`,
      );
      if (response.data.status) {
        setNotifications(notifications.map((n) => ({ ...n, is_read: true })));
        setUnreadCount(0);
      }
    } catch (error) {
      console.error("Error marking all as read:", error);
    }
  };

  const markAsRead = async (id) => {
    try {
      const response = await apiClient.put(
        `/notifications/update-notification-ById/${id}`,
        { is_read: true },
      );
      if (response.data.status) {
        setNotifications((prev) =>
          prev.map((n) => (n.id === id ? { ...n, is_read: true } : n)),
        );
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error("Error updating notification:", error);
    }
  };

  // Derive unread count dynamically from the updated local state array
  const unreadCountUI = notifications.filter((n) => !n.is_read).length;

  const getIcon = (title) => {
    const t = title.toLowerCase();
    if (t.includes("treat")) return <Heart className="notif-ui-icon heart" />;
    if (t.includes("meet") || t.includes("walk"))
      return <MessageSquare className="notif-ui-icon message" />;
    if (t.includes("event"))
      return <Calendar className="notif-ui-icon calendar" />;
    if (t.includes("request"))
      return <UserPlus className="notif-ui-icon user" />;
    return <MessageSquare className="notif-ui-icon default" />;
  };

  if (loading)
    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background:
            "linear-gradient(139.27deg, #009fd9 23.13%, #005b7d 98.96%)",
          zIndex: 9999,
        }}
      >
        <Loader2 className="animate-spin" size={40} />
      </div>
    );

  return (
    <div className="notif-ui-wrapper">
      {/* DESKTOP Top Navigation Panel */}
      <div className="notif-ui-navbar notif-desktop-only">
        <div
          className="notif-ui-navbar-div"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            cursor: "pointer",
            padding: "22px",
          }}
          onClick={() => navigate("/dashboard-switcher")}
        >
          <img
            src={appIcon}
            alt="Social Paws"
            style={{ width: "28px", height: "28px", objectFit: "contain" }}
          />
          <span
            style={{
              color: "#fff",
              fontSize: "22px",
              fontWeight: "700",
            }}
          >
            Social Paws
          </span>
        </div>

        <h1 className="notif-ui-page-title">Notifications</h1>

        <div className="notif-ui-nav-icons">
          <TopNavBar theme="dark" />
        </div>
      </div>

      {/* MOBILE Top Navigation Panel */}
      <div className="notif-ui-navbar-mobile notif-mobile-only">
        <MobileTopBar
          onBack={() => navigate("/dashboard-switcher")}
          backColor="#fff"
          logoColor="#fff"
        />

        <div className="notif-mobile-header-bottom">
          <div className="notif-ui-nav-icons">
            <TopNavBar theme="dark" />
          </div>
        </div>
      </div>

      <div className="notif-ui-card">
        {isMobile && <h1 className="notif-mobile-page-title">Notifications</h1>}

        <div className="notif-ui-body">
          <div className="notif-ui-header-row">
            <span className="notif-ui-count">
              You have {unreadCountUI} unread notifications
            </span>
            <button className="notif-ui-mark-all" onClick={markAllAsRead}>
              Mark all as read
            </button>
          </div>

          <div className="notif-ui-tabs">
            {["All"].map((tab) => (
              <button
                key={tab}
                className={`notif-ui-tab-btn ${activeTab === tab ? "active" : ""}`}
                onClick={() => setActiveTab(tab)}
              >
                {tab}
              </button>
            ))}
          </div>

          <div className="notif-ui-list">
            {loading ? (
              <p className="notif-ui-empty">Loading...</p>
            ) : notifications.length > 0 ? (
              notifications.map((notif) => (
                <div
                  key={notif.id}
                  className={`notif-ui-item ${!notif.is_read ? "unread" : ""}`}
                  onClick={() => {
                    if (!notif.is_read) markAsRead(notif.id);

                    const title = (notif.title || "").toLowerCase();

                    if (title.includes("treat")) {
                      navigate("/receive-a-treat", {
                        state: { notificationId: notif.id },
                      });
                    } else if (title.includes("walk")) {
                      navigate("/receive-a-book", {
                        state: {
                          notificationId: notif.id,
                        },
                      });
                    }
                  }}
                >
                  <div
                    className={`notif-ui-icon-circle ${(notif.title || "").toLowerCase().includes("treat") ? "treat-bg" : "blue-bg"}`}
                  >
                    {getIcon(notif.title)}
                  </div>

                  {!notif.is_read && <div className="notif-ui-unread-dot" />}

                  <div className="notif-ui-content">
                    <div className="notif-ui-top">
                      <span className="notif-ui-sender">{notif.title}</span>
                    </div>
                    <p className="notif-ui-msg">{notif.message}</p>
                    <span className="notif-ui-time">
                      {formatDistanceToNow(new Date(notif.created_at))} ago
                    </span>
                  </div>

                  <div className="notif-ui-actions">
                    <button
                      className={`notif-ui-btn ${notif.is_read ? "tertiary" : "primary"}`}
                    >
                      {(notif.title || "").toLowerCase().includes("treat")
                        ? "View Treat"
                        : (notif.title || "").toLowerCase().includes("walk")
                          ? "View Booking"
                          : "Reply"}
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <p className="notif-ui-empty">No notifications yet.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Notifications;
